import { useEffect, useRef, useState } from 'react';

interface VideoBackgroundProps {
  videoUrl: string;
  opacity?: number;
  blur?: number;
  gradientColors?: string[];
}

export function VideoBackground({ 
  videoUrl, 
  opacity = 0.5, 
  blur = 2,
  gradientColors = ['#1a1d21', '#121416']
}: VideoBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Use gradient colors to create a unique gradient for each slide
  const gradientStart = gradientColors[0] || '#1a1d21';
  const gradientEnd = gradientColors[1] || '#121416';

  useEffect(() => {
    // Reset states when video URL changes
    setIsLoaded(false);
    setHasError(false);
    
    if (!videoUrl || !videoRef.current) {
      // If no video URL is provided, treat it as an error case
      setHasError(true);
      return;
    }
    
    // Set up event listeners
    const video = videoRef.current;
    
    const handleCanPlay = () => {
      setIsLoaded(true);
      video.play().catch(error => {
        console.error('Failed to auto-play video:', error);
        setHasError(true);
      });
    };
    
    const handleError = (error: Event) => {
      console.error('Video loading error:', error);
      setHasError(true);
    };
    
    // Add event listeners
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    
    // Attempt to load and play the video
    video.load();
    
    // Clean up event listeners
    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl]);

  // Always render a gradient background
  return (
    <div className="video-background">
      {videoUrl && !hasError && (
        <video 
          ref={videoRef}
          src={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          crossOrigin="anonymous"
          style={{ display: isLoaded ? 'block' : 'none' }}
        />
      )}
      <div className="overlay"></div>
      <style>{`
        .video-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 0;
          background: linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%);
        }
        
        .video-background video {
          position: absolute;
          top: 50%;
          left: 50%;
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          transform: translateX(-50%) translateY(-50%);
          object-fit: cover;
          opacity: ${isLoaded ? 0.7 : 0};
          transition: opacity 0.5s ease;
        }

        .overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(26, 29, 33, ${opacity}) 0%, rgba(18, 20, 22, ${opacity + 0.2}) 100%);
          backdrop-filter: blur(${blur}px);
          -webkit-backdrop-filter: blur(${blur}px);
          z-index: 1;
        }
      `}</style>
    </div>
  );
} 